import React from "react";
import IconHome from "../../../styles/icons/IconHome";

export interface OperatorPerformance {
  id: number;
  profilePictureUrl: string;
  firstName: string;
  lastName: string;
  totalLeads: number;
  hotLeadsCount: number;
  warmLeadsCount: number;
  interestedLeadsCount: number;
  rejectedLeadsCount: number;
  hotWoarmLeadsPercentage: number;
  rejectedLeadsPercentage: number;
  performanceType: number;
}

type CardVariant =
  | "all-leads"
  | "hot"
  | "warm"
  | "interested"
  | "success"
  | "light";

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: string;
  variant: CardVariant;
  isPercentage?: boolean;
  className?: string;
  subTitle?: string;
}

interface OperatorPerformanceSummaryProps {
  data: OperatorPerformance;
  isHideTtitle?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  variant,
  isPercentage = false,
  className = "",
  subTitle,
}) => {
  return (
    <div
      className={`operator-performance-card operator-performance-card--${variant} ${className}`}
    >
      <div className="operator-performance-card__header">
        {icon && (
          <div className="operator-performance-card__icon">
            <IconHome
              breadcrumb={icon}
              activeColor="#0f1560"
              bgColor="transparent"
            />
          </div>
        )}
        <h3 className="operator-performance-card__title">{title}</h3>
      </div>

      <div className="operator-performance-card__value">{value}</div>

      <p className="operator-performance-card__sub-title">{subTitle}</p>
    </div>
  );
};

const OperatorPerformanceSummary: React.FC<OperatorPerformanceSummaryProps> = ({
  data,
}) => {
  const totalLeads = data?.totalLeads ?? 0;
  const highIntentCount = data?.hotLeadsCount ?? 0;
  const lowIntentCount = data?.interestedLeadsCount ?? 0;

  const getPercentage = (value: number, total: number) => {
    if (!total) return "0.00%";
    return `${((value / total) * 100).toFixed(2)}%`;
  };

  const highIntentRate = getPercentage(highIntentCount, totalLeads);
  const lowIntentRate = getPercentage(lowIntentCount, totalLeads);

  const stats: StatCardProps[] = [
    {
      title: "All Leads",
      value: data.totalLeads ?? 0,
      icon: "allLeads",
      variant: "all-leads",
      subTitle: "Tracking all leads",
    },
    {
      title: "High Intent",
      value: data.hotLeadsCount ?? 0,
      icon: "hotLeads",
      variant: "hot",
      subTitle: "AI classified",
    },
    {
      title: "Medium Intent",
      value: data.warmLeadsCount ?? 0,
      icon: "warmLeads",
      variant: "warm",
      subTitle: "AI classified",
    },
    {
      title: "Low Intent",
      value: data.interestedLeadsCount ?? 0,
      icon: "interestedLeads",
      variant: "interested",
      subTitle: "AI classified",
    },
    {
      title: "High Intent Rate",
      value: (data.hotWoarmLeadsPercentage ?? 0) + " %",
      variant: "success",
      isPercentage: true,
      icon: "highIntentRateIcon",
      subTitle: "Confidence-based",
    },
    {
      title: "Rejected Leads Rate",
      value: (data.rejectedLeadsCount ?? 0) + " %",
      variant: "light",
      icon: "lowIntentPercentageIcon",
      isPercentage: true,
      subTitle: "AI classified",
    },
  ];

  return (
    <section className="operator-performance-summary">
      <div className="operator-performance-summary__grid">
        {stats.map((item, index) => (
          <StatCard
            key={index}
            title={item.title}
            value={item.value}
            icon={item.icon}
            variant={item.variant}
            isPercentage={item.isPercentage}
            className={item.className}
            subTitle={item.subTitle}
          />
        ))}
      </div>
    </section>
  );
};

export default OperatorPerformanceSummary;
